import React, { useEffect, useState } from 'react';
import { Typography, Button, Tag, Modal, Row, Col } from 'antd';
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    ExclamationCircleOutlined,
    CreditCardOutlined,
    ThunderboltOutlined,
    SafetyCertificateOutlined,
    RocketOutlined,
    CustomerServiceOutlined,
} from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { activateSubscription } from '../../components/guards/subscriptionActions';
import { SUBSCRIPTION_STATUS } from '../../constants';
import { notifyError, notifySuccess } from '../../utils/notification';
import { StripeIPG } from '../../components/IPG/StripeIPG';
import { getUserAccounts } from '../main/actions';
import { getCardList } from '../testcaseCreation/action';
import Loading from '../../components/Loading';

import './BillingPage.scss';

const { Title, Text, Paragraph } = Typography;

const PLAN_PRICE = '$99';
const PLAN_INTERVAL = 'month';
const PLAN_NAME = 'Professional';
const TRIAL_TOTAL_DAYS = 14;

const PLAN_FEATURES = [
    {
        icon: <ThunderboltOutlined />,
        title: 'Unlimited Test Cases',
        description: 'Create and run unlimited automated test cases across all your projects.',
    },
    {
        icon: <SafetyCertificateOutlined />,
        title: 'Priority Support',
        description: 'Get dedicated support from our engineering team with faster response times.',
    },
    {
        icon: <RocketOutlined />,
        title: 'Advanced Reporting',
        description: 'Detailed analytics dashboards and exportable test execution reports.',
    },
    {
        icon: <CustomerServiceOutlined />,
        title: 'Team Collaboration',
        description: 'Invite team members to your workspace and collaborate in real time.',
    },
];

const BillingPage = () => {
    const dispatch = useAppDispatch();
    const userId = useAppSelector(({ userData }) => userData.userId);
    const { status, trialEnd, isActive } = useAppSelector(
        ({ subscriptionData }) => subscriptionData
    );
    const selectedAccount = useAppSelector(
        ({ userData }: any) => userData.selectedAccount
    );

    const [isCardModalOpen, setIsCardModalOpen] = useState(false);
    const [isActivating, setIsActivating] = useState(false);
    const [availableCards, setAvailableCards] = useState<any[]>([]);
    const [isCardLoading, setCardLoading] = useState(false);

    const fetchCards = async () => {
        if (selectedAccount?.customerId) {
            setCardLoading(true);
            try {
                const cardList = await getCardList(selectedAccount.customerId);
                setAvailableCards(cardList);
            } catch (e) {
                console.error('Failed to fetch cards:', e);
            } finally {
                setCardLoading(false);
            }
        }
    };

    useEffect(() => {
        fetchCards();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAccount?.customerId]);

    const handleCardAdded = async () => {
        setIsCardModalOpen(false);
        await getUserAccounts(userId, dispatch);
        await fetchCards();
        notifySuccess('Card updated successfully!', 'card_updated');
    };

    const handleActivate = async () => {
        if (!selectedAccount?.accountId) return;
        try {
            setIsActivating(true);
            await activateSubscription(selectedAccount.accountId, dispatch);
            notifySuccess('Subscription activated!', 'subscription_activated');
        } catch {
            notifyError('Failed to activate subscription.', 'subscription_activation_error');
        } finally {
            setIsActivating(false);
        }
    };

    const getTrialProgress = () => {
        if (!trialEnd) return { daysLeft: 0, daysUsed: TRIAL_TOTAL_DAYS, percentage: 100 };
        const end = new Date(trialEnd);
        const now = new Date();
        const daysLeft = Math.max(0, Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
        const daysUsed = TRIAL_TOTAL_DAYS - daysLeft;
        const percentage = Math.min(100, (daysUsed / TRIAL_TOTAL_DAYS) * 100);
        return { daysLeft, daysUsed, percentage };
    };

    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    const renderTrialView = () => {
        const { daysLeft, daysUsed, percentage } = getTrialProgress();

        return (
            <div className="billing-page__content">
                <Row gutter={[32, 32]}>
                    <Col xs={24} lg={10}>
                        <div className="billing-card billing-card--trial">
                            <div className="billing-card__badge">
                                <ClockCircleOutlined />
                                <span>FREE TRIAL</span>
                            </div>
                            <Title level={3} className="billing-card__heading">
                                Your free trial is{' '}
                                <span className="billing-card__accent">active</span>
                            </Title>
                            <Paragraph className="billing-card__description">
                                In {daysLeft} day{daysLeft !== 1 ? 's' : ''}, your access to the platform
                                will be paused. Add a payment method and subscribe to continue seamlessly.
                            </Paragraph>

                            <div className="trial-progress">
                                <div className="trial-progress__header">
                                    <Text className="trial-progress__label">Trial Usage</Text>
                                    <Text className="trial-progress__count">
                                        {daysUsed} / {TRIAL_TOTAL_DAYS} Days
                                    </Text>
                                </div>
                                <div className="trial-progress__bar">
                                    <div
                                        className="trial-progress__fill"
                                        style={{ width: `${percentage}%` }}
                                    />
                                </div>
                                <Text className="trial-progress__end-date">
                                    Trial ends on {formatDate(trialEnd)}
                                </Text>
                            </div>

                            {availableCards.length > 0 && (
                                <div className="payment-ready">
                                    <div className="payment-ready__indicator">
                                        <CheckCircleOutlined className="payment-ready__check" />
                                        <Text className="payment-ready__label">Payment Ready</Text>
                                    </div>
                                    <div className="payment-ready__card-info">
                                        <CreditCardOutlined className="payment-ready__card-icon" />
                                        <Text className="payment-ready__card-number">
                                            •••• {availableCards[0]?.card?.last4}
                                        </Text>
                                        <Text className="payment-ready__card-expiry">
                                            {availableCards[0]?.card?.exp_month}/{availableCards[0]?.card?.exp_year}
                                        </Text>
                                    </div>
                                </div>
                            )}

                            <div className="trial-timeline">
                                <Text className="trial-timeline__title">What happens next</Text>
                                <div className="trial-timeline__steps">
                                    <div className="trial-timeline__step trial-timeline__step--active">
                                        <div className="trial-timeline__dot" />
                                        <div className="trial-timeline__content">
                                            <Text className="trial-timeline__step-title">Trial Active</Text>
                                            <Text className="trial-timeline__step-desc">Full access to all features</Text>
                                        </div>
                                    </div>
                                    <div className="trial-timeline__step">
                                        <div className="trial-timeline__dot" />
                                        <div className="trial-timeline__content">
                                            <Text className="trial-timeline__step-title">Trial Ends — {formatDate(trialEnd)}</Text>
                                            <Text className="trial-timeline__step-desc">
                                                {availableCards.length > 0
                                                    ? 'Your card will be charged automatically'
                                                    : 'Add a payment method to avoid interruption'}
                                            </Text>
                                        </div>
                                    </div>
                                    <div className="trial-timeline__step">
                                        <div className="trial-timeline__dot" />
                                        <div className="trial-timeline__content">
                                            <Text className="trial-timeline__step-title">Subscription Starts</Text>
                                            <Text className="trial-timeline__step-desc">{PLAN_PRICE}/{PLAN_INTERVAL}, billed monthly</Text>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Col>

                    <Col xs={24} lg={14}>
                        <div className="billing-card billing-card--plan">
                            <div className="plan-header">
                                <Text className="plan-header__label">YOUR PLAN</Text>
                                <Title level={3} className="plan-header__title">
                                    {PLAN_NAME} Plan - {' '}
                                    <span className="plan-header__accent">{PLAN_PRICE}/{PLAN_INTERVAL}</span>
                                </Title>
                            </div>

                            <div className="plan-features">
                                {PLAN_FEATURES.map((feature, i) => (
                                    <div key={i} className="plan-feature">
                                        <div className="plan-feature__icon">{feature.icon}</div>
                                        <div className="plan-feature__content">
                                            <Text className="plan-feature__title">{feature.title}</Text>
                                            <Text className="plan-feature__desc">{feature.description}</Text>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="plan-pricing">
                                <div className="plan-pricing__amount">
                                    <span className="plan-pricing__price">{PLAN_PRICE}</span>
                                    <span className="plan-pricing__interval"> / {PLAN_INTERVAL}</span>
                                </div>
                                <Tag className="plan-pricing__tag">{PLAN_NAME.toUpperCase()}</Tag>
                            </div>

                            <Button
                                className={`billing-btn ${availableCards.length > 0 ? 'billing-btn--outline' : 'billing-btn--primary'}`}
                                icon={<CreditCardOutlined />}
                                onClick={() => setIsCardModalOpen(true)}
                                block
                                size="large"
                            >
                                {availableCards.length > 0
                                    ? 'Update Payment Method'
                                    : 'Add Payment Method'}
                            </Button>
                            <Text className="plan-note">
                                {availableCards.length > 0
                                    ? 'You\'re all set! Your billing cycle begins automatically when your trial ends.'
                                    : 'Add a payment method now to ensure uninterrupted access after your trial.'}
                            </Text>
                        </div>
                    </Col>
                </Row>
            </div>
        );
    };

    const renderActiveView = () => (
        <div className="billing-page__content">
            <Row gutter={[32, 32]}>
                <Col xs={24} lg={14}>
                    <div className="billing-card billing-card--active">

                        <div className="active-card__header">
                            <div className="active-card__title-row">
                                <div>
                                    <p className="active-card__eyebrow">Current Plan</p>
                                    <Title level={3} className="active-card__title">
                                        {PLAN_NAME}{' '}
                                        <span className="active-card__price">
                                            {PLAN_PRICE}
                                            <span className="active-card__interval">/{PLAN_INTERVAL}</span>
                                        </span>
                                    </Title>
                                </div>
                                <div className="active-status-pill">
                                    <span className="active-status-pill__dot" />
                                    Active
                                </div>
                            </div>
                        </div>

                        <div className="active-info-grid">
                            <div className="active-info-grid__item">
                                <span className="active-info-grid__label">Plan</span>
                                <span className="active-info-grid__value">{PLAN_NAME}</span>
                            </div>
                            <div className="active-info-grid__item">
                                <span className="active-info-grid__label">Billing</span>
                                <span className="active-info-grid__value">Monthly</span>
                            </div>
                            <div className="active-info-grid__item">
                                <span className="active-info-grid__label">Price</span>
                                <span className="active-info-grid__value active-info-grid__value--accent">
                                    {PLAN_PRICE}/{PLAN_INTERVAL}
                                </span>
                            </div>
                            <div className="active-info-grid__item">
                                <span className="active-info-grid__label">Status</span>
                                <span className="active-info-grid__value active-info-grid__value--green">
                                    <CheckCircleOutlined style={{ marginRight: 5 }} />
                                    Active
                                </span>
                            </div>
                        </div>

                        <div className="billing-card__payment">
                            <Text className="billing-card__section-title">Payment Method</Text>
                            {isCardLoading ? (
                                <Loading plain size="small" />
                            ) : availableCards.length > 0 ? (
                                <div className="payment-card">
                                    <CreditCardOutlined className="payment-card__icon" />
                                    <div className="payment-card__details">
                                        <Text className="payment-card__number">
                                            •••• •••• •••• {availableCards[0]?.card?.last4}
                                        </Text>
                                        <Text className="payment-card__expiry">
                                            Expires {availableCards[0]?.card?.exp_month}/{availableCards[0]?.card?.exp_year}
                                        </Text>
                                    </div>
                                    <Button
                                        className="billing-btn billing-btn--outline billing-btn--sm"
                                        icon={<CreditCardOutlined />}
                                        onClick={() => setIsCardModalOpen(true)}
                                    >
                                        Update
                                    </Button>
                                </div>
                            ) : (
                                <Button
                                    className="billing-btn billing-btn--outline"
                                    icon={<CreditCardOutlined />}
                                    onClick={() => setIsCardModalOpen(true)}
                                >
                                    Add Payment Method
                                </Button>
                            )}
                        </div>
                    </div>
                </Col>

                <Col xs={24} lg={10}>
                    <div className="billing-card billing-card--features-active">
                        <Title level={4} className="billing-card__section-title">
                            What's Included
                        </Title>
                        <div className="plan-features plan-features--compact">
                            {PLAN_FEATURES.map((feature, i) => (
                                <div key={i} className="plan-feature plan-feature--compact">
                                    <div className="plan-feature__icon plan-feature__icon--green">
                                        {feature.icon}
                                    </div>
                                    <div className="plan-feature__content">
                                        <Text className="plan-feature__title">{feature.title}</Text>
                                        <Text className="plan-feature__desc">{feature.description}</Text>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </Col>
            </Row>
        </div>
    );

    const renderInactiveView = () => {
        const statusConfigs: Record<string, {
            icon: React.ReactNode;
            badge: string;
            badgeClass: string;
            heading: React.ReactNode;
            description: string;
            ctaText: string;
        }> = {
            [SUBSCRIPTION_STATUS.PAUSED]: {
                icon: <ClockCircleOutlined />,
                badge: 'TRIAL ENDED',
                badgeClass: 'billing-card__badge--warning',
                heading: <>Your free trial has <span className="billing-card__accent--orange">ended</span></>,
                description: 'Your trial period has expired. Subscribe now to regain full access to all platform features and continue your work without interruption.',
                ctaText: 'Subscribe & Reactivate',
            },
            [SUBSCRIPTION_STATUS.PAST_DUE]: {
                icon: <ExclamationCircleOutlined />,
                badge: 'PAYMENT FAILED',
                badgeClass: 'billing-card__badge--warning',
                heading: <>Your payment has <span className="billing-card__accent--orange">failed</span></>,
                description: 'We were unable to process your last payment. Please update your payment method to restore access to all platform features.',
                ctaText: 'Update Payment & Reactivate',
            },
            [SUBSCRIPTION_STATUS.CANCELED]: {
                icon: <ExclamationCircleOutlined />,
                badge: 'SUBSCRIPTION CANCELED',
                badgeClass: 'billing-card__badge--danger',
                heading: <>Your subscription has been <span className="billing-card__accent--red">canceled</span></>,
                description: 'Your subscription has been canceled and access to platform features is restricted. Resubscribe anytime to pick up right where you left off.',
                ctaText: 'Resubscribe Now',
            },
            [SUBSCRIPTION_STATUS.INCOMPLETE]: {
                icon: <ExclamationCircleOutlined />,
                badge: 'SETUP INCOMPLETE',
                badgeClass: 'billing-card__badge--danger',
                heading: <>Your subscription setup is <span className="billing-card__accent--red">incomplete</span></>,
                description: 'Your subscription setup could not be completed. Please add a payment method and activate to gain full access to all features.',
                ctaText: 'Complete Setup',
            },
        };

        const config = statusConfigs[status] || statusConfigs[SUBSCRIPTION_STATUS.CANCELED];

        return (
            <div className="billing-page__content">
                <Row gutter={[32, 32]}>
                    <Col xs={24} lg={10}>
                        <div className="billing-card billing-card--inactive">
                            <div className={`billing-card__badge ${config.badgeClass}`}>
                                {config.icon}
                                <span>{config.badge}</span>
                            </div>
                            <Title level={3} className="billing-card__heading">
                                {config.heading}
                            </Title>
                            <Paragraph className="billing-card__description">
                                {config.description}
                            </Paragraph>

                            <div className="billing-card__payment">
                                {isCardLoading ? (
                                    <Loading plain size="small" />
                                ) : availableCards.length > 0 ? (
                                    <div className="payment-card">
                                        <CreditCardOutlined className="payment-card__icon" />
                                        <div className="payment-card__details">
                                            <Text className="payment-card__number">
                                                •••• {availableCards[0]?.card?.last4}
                                            </Text>
                                            <Text className="payment-card__expiry">
                                                {availableCards[0]?.card?.exp_month}/{availableCards[0]?.card?.exp_year}
                                            </Text>
                                        </div>
                                        <Button
                                            type="link"
                                            size="small"
                                            onClick={() => setIsCardModalOpen(true)}
                                        >
                                            Update
                                        </Button>
                                    </div>
                                ) : (
                                    <Button
                                        className="billing-btn billing-btn--outline"
                                        icon={<CreditCardOutlined />}
                                        onClick={() => setIsCardModalOpen(true)}
                                        block
                                    >
                                        Add Payment Method
                                    </Button>
                                )}
                            </div>

                            <Button
                                className="billing-btn billing-btn--primary"
                                onClick={handleActivate}
                                loading={isActivating}
                                disabled={availableCards.length === 0}
                                block
                                size="large"
                                style={{ marginTop: 16 }}
                            >
                                {config.ctaText}
                            </Button>
                            {availableCards.length === 0 && (
                                <Text className="plan-note">
                                    Add a payment method to continue.
                                </Text>
                            )}
                        </div>
                    </Col>

                    <Col xs={24} lg={14}>
                        <div className="billing-card billing-card--plan">
                            <div className="plan-header">
                                <Text className="plan-header__label">REACTIVATE ACCESS</Text>
                                <Title level={3} className="plan-header__title">
                                    Get Back to Building.{' '}
                                    <span className="plan-header__accent">Subscribe Now.</span>
                                </Title>
                            </div>

                            <div className="plan-features">
                                {PLAN_FEATURES.map((feature, i) => (
                                    <div key={i} className="plan-feature">
                                        <div className="plan-feature__icon">{feature.icon}</div>
                                        <div className="plan-feature__content">
                                            <Text className="plan-feature__title">{feature.title}</Text>
                                            <Text className="plan-feature__desc">{feature.description}</Text>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="plan-pricing">
                                <div className="plan-pricing__amount">
                                    <span className="plan-pricing__price">{PLAN_PRICE}</span>
                                    <span className="plan-pricing__interval"> / {PLAN_INTERVAL}</span>
                                </div>
                                <Tag className="plan-pricing__tag">{PLAN_NAME.toUpperCase()}</Tag>
                            </div>
                        </div>
                    </Col>
                </Row>
            </div>
        );
    };

    const renderContent = () => {
        if (status === SUBSCRIPTION_STATUS.TRIALING) return renderTrialView();
        if (status === SUBSCRIPTION_STATUS.ACTIVE) return renderActiveView();
        return renderInactiveView();
    };

    return (
        <div className="billing-page">
            <div className="billing-page__header">
                <Title level={2} className="billing-page__title">Manage Subscription</Title>
            </div>

            {renderContent()}

            <Modal
                className="fp_profile_modal"
                style={{ top: '10%' }}
                open={isCardModalOpen}
                onCancel={() => setIsCardModalOpen(false)}
                closable={false}
                footer={null}
            >
                <StripeIPG
                    accountId={selectedAccount?.accountId}
                    handleOk={handleCardAdded}
                    onCancel={() => setIsCardModalOpen(false)}
                    profileview
                />
            </Modal>
        </div>
    );
};

export default BillingPage;
